/**
 * The page's one subscription to the local server's `/api/event` stream,
 * and what it keeps from it: each session's raw events since its latest observed
 * turn start, up to a cap. An observed start creates or resets the tail; only
 * success clears it at turn end. Failed or interrupted tails keep later events
 * until the next observed start, resync, or session deletion. Deletion clears
 * the raw tail, missed-start marker, and per-turn revision. Events without a
 * tail are relayed without adding history, but can mark the session's start as
 * missed. A page that loads a session gets this tail with its snapshot
 * (`operations.ts`), so text in progress survives a reload, which OpenCode's
 * stored messages hold as empty until a part ends. The stream is
 * volatile by contract: the server drops a subscriber after 4,096 buffered
 * events and the page's reconnect misses what happened in between. After a
 * reconnect every subscriber gets `resync`, and the page reloads the session.
 */
import type { OpenCodeEvent, OpenCodeServerAddress } from './opencode-api';

import { EVENT_ROUTE, OpenCodeTimeoutError, sessionIdOf } from './opencode-api';

export type TailSignal = { kind: 'event'; event: OpenCodeEvent } | { kind: 'resync' };

export type TailListener = (signal: TailSignal) => void;

/**
 * Whether a signal may change what the sessions wait on the person for: a
 * permission request (`permission.asked`, `permission.replied`) or a form
 * (`form.created`, `form.replied`, `form.cancelled`) that starts or ends, or a
 * reconnect, which may have missed one.
 */
export function changesAsks(signal: TailSignal): boolean {
  if (signal.kind === 'resync') {
    return true;
  }
  switch (signal.event.type) {
    case 'permission.asked':
    case 'permission.replied':
    case 'form.created':
    case 'form.replied':
    case 'form.cancelled':
      return true;
    default:
      return false;
  }
}

/** How many events one session's tail keeps; the oldest go first. */
export const EVENT_TAIL_CAPACITY = 20_000;

/** The event that starts a turn and so resets the session's tail. */
const TURN_START_EVENT = 'session.execution.started';

/**
 * The event that ends a turn with its messages stored whole, so the tail is
 * dropped. A failed or interrupted turn can leave streamed text unfinished in
 * the stored message, so its tail stays.
 */
const TURN_SUCCEEDED_EVENT = 'session.execution.succeeded';

type Tail = { events: OpenCodeEvent[]; truncated: boolean };

export class EventTail {
  private readonly tails = new Map<string, Tail>();
  private revisionCounter = 0;
  private readonly turnRevisions = new Map<string, number>();
  private resetRevision = 0;
  /** Sessions whose events arrived with no open tail, so their turn start was missed. */
  private readonly missedStart = new Set<string>();
  private readonly listeners = new Set<TailListener>();

  constructor(private readonly capacity = EVENT_TAIL_CAPACITY) {}

  /** Applies an upstream event to its session's tail, then relays it to every subscriber. */
  push(event: OpenCodeEvent) {
    const sessionId = sessionIdOf(event);
    if (sessionId !== null) {
      if (event.type === 'session.deleted') {
        this.tails.delete(sessionId);
        this.missedStart.delete(sessionId);
        this.turnRevisions.delete(sessionId);
      } else if (event.type === TURN_SUCCEEDED_EVENT) {
        this.tails.delete(sessionId);
        this.missedStart.delete(sessionId);
      } else if (event.type === TURN_START_EVENT) {
        this.turnRevisions.set(sessionId, ++this.revisionCounter);
        this.tails.set(sessionId, { events: [event], truncated: false });
        this.missedStart.delete(sessionId);
      } else {
        const tail = this.tails.get(sessionId);
        if (tail === undefined) {
          this.missedStart.add(sessionId);
        } else {
          tail.events.push(event);
          if (tail.events.length > this.capacity) {
            tail.events.splice(0, tail.events.length - this.capacity);
            tail.truncated = true;
          }
        }
      }
    }
    this.notify({ kind: 'event', event });
  }

  /**
   * The upstream stream reconnected, so events were missed. Every tail is
   * dropped, because a tail with a hole would replay wrong text, and each
   * subscriber is told to load its sessions again.
   */
  resync() {
    this.tails.clear();
    this.missedStart.clear();
    this.turnRevisions.clear();
    this.resetRevision = ++this.revisionCounter;
    this.notify({ kind: 'resync' });
  }

  /**
   * The session's retained events since its latest observed turn start, oldest
   * first, up to the cap; empty when the session has no tail.
   */
  eventsFor(sessionId: string): readonly OpenCodeEvent[] {
    return this.tails.get(sessionId)?.events ?? [];
  }

  /**
   * The latest observed turn revision, retained after success to fence snapshot
   * reads. Deleted sessions or sessions with no observed turn revision fall back
   * to the reset revision (initially zero, updated on resync).
   */
  turnRevisionFor(sessionId: string): number {
    return this.turnRevisions.get(sessionId) ?? this.resetRevision;
  }

  /**
   * Whether the session's tail is incomplete: the cap dropped its oldest events,
   * or events arrived after a reconnect for a turn whose start the stream
   * missed, so the page must rebuild from the stored messages.
   */
  truncatedFor(sessionId: string) {
    return (this.tails.get(sessionId)?.truncated ?? false) || this.missedStart.has(sessionId);
  }

  subscribe(listener: TailListener) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notify(signal: TailSignal) {
    for (const listener of this.listeners) {
      try {
        listener(signal);
      } catch (error) {
        console.error('Local agent event listener failed', error);
      }
    }
  }
}

export type EventStreamOptions = {
  /**
   * Opens one `/api/event` subscription to `server`, through OpenCode's own
   * client in the page and the driver (`subscribeEvents` in `operations.ts`).
   * It ends quietly when `signal` aborts, calls `onActivity` for every chunk
   * the server sends, heartbeats included, and fails with cubic's own errors
   * (`opencode-client.ts`).
   */
  subscribe: (
    server: OpenCodeServerAddress,
    options: { signal: AbortSignal; onActivity: () => void },
  ) => AsyncIterable<OpenCodeEvent>;
  /** The running server, or null while none runs; the loop asks again every 250 ms. */
  server: () => OpenCodeServerAddress | null;
  tail: EventTail;
  signal: AbortSignal;
  sleep: (ms: number) => Promise<void>;
  /**
   * Called with each connection's failure, for a log line: what the
   * subscription threw, or an `OpenCodeTimeoutError` for one the watchdog
   * dropped for its silence.
   */
  onError: (error: unknown) => void;
  /** True once the server acknowledges a connection (`server.connected`), false when it ends. */
  onConnectionChange?: (connected: boolean) => void;
  /**
   * Hands the caller, once, a way to drop the connection the loop holds: the
   * page drops it when a start hands it another server. A dropped connection,
   * like one the watchdog ends, connects again to `server()` and resyncs,
   * which clears every tail and so the text a turn has streamed so far, since
   * OpenCode never replays it; a drop while the loop waits to reconnect ends
   * the wait.
   */
  watch?: (drop: () => void) => void;
};

/** The waits before the first reconnects; every later one waits the cap. */
const RECONNECT_DELAYS_MS = [250, 500, 1_000, 2_000] as const;
const RECONNECT_DELAY_CAP_MS = 5_000;
const NO_SERVER_POLL_MS = 250;

/**
 * How long a connection may go without a byte before it is taken for lost:
 * three of the heartbeats OpenCode 2.0.26 sends every 15 s
 * (`packages/server/src/handlers/event.ts`).
 */
export const STREAM_SILENCE_MS = 45_000;

function reconnectDelay(attempt: number): number {
  return RECONNECT_DELAYS_MS[attempt] ?? RECONNECT_DELAY_CAP_MS;
}

/**
 * Keeps one `/api/event` subscription open for as long as `signal` lives.
 * The first connection fills the tail from then on; every later connection,
 * which means events were missed, calls `tail.resync()` once the server has
 * acknowledged it with `server.connected`. A connection that sends nothing,
 * not even a heartbeat, for `STREAM_SILENCE_MS` is dropped and made again: a
 * server that stopped, or a laptop that slept, can leave it open and silent.
 */
export async function runEventStream(options: EventStreamOptions) {
  let connections = 0;
  let attempt = 0;
  let current: AbortController | null = null;
  // Ends the wait before a reconnect, while the loop waits.
  let wake: (() => void) | null = null;
  options.watch?.(() => {
    current?.abort();
    wake?.();
  });
  while (!options.signal.aborted) {
    const server = options.server();
    if (server === null) {
      await options.sleep(NO_SERVER_POLL_MS);
      continue;
    }
    const connection = new AbortController();
    current = connection;
    const drop = () => connection.abort();
    options.signal.addEventListener('abort', drop, { once: true });
    const silent = () => {
      options.onError(new OpenCodeTimeoutError(EVENT_ROUTE, STREAM_SILENCE_MS));
      drop();
    };
    let silence = setTimeout(silent, STREAM_SILENCE_MS);
    const onActivity = () => {
      clearTimeout(silence);
      silence = setTimeout(silent, STREAM_SILENCE_MS);
    };
    try {
      for await (const event of options.subscribe(server, {
        signal: connection.signal,
        onActivity,
      })) {
        if (event.type === 'server.connected') {
          connections += 1;
          attempt = 0;
          options.onConnectionChange?.(true);
          if (connections > 1) {
            options.tail.resync();
          }
          continue;
        }
        options.tail.push(event);
      }
    } catch (error) {
      if (options.signal.aborted) {
        return;
      }
      // A connection this loop dropped ends as it was told to; any other end is a failure.
      if (!connection.signal.aborted) {
        options.onError(error);
      }
    } finally {
      clearTimeout(silence);
      options.signal.removeEventListener('abort', drop);
      current = null;
      options.onConnectionChange?.(false);
    }
    if (options.signal.aborted) {
      return;
    }
    const delay = reconnectDelay(attempt);
    attempt += 1;
    await Promise.race([
      options.sleep(delay),
      new Promise<void>((resolve) => {
        wake = resolve;
      }),
    ]);
    wake = null;
  }
}
